import React, { useCallback, useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { openSupportEmail } from "../utils/supportActions";
import Pagination from "./Pagination";
import SubmitSupportTicketModal from "./SubmitSupportTicketModal";
import SupportTicketDetailModal from "./SupportTicketDetailModal";
import SupportTicketSelect from "./SupportTicketSelect";
import {
    fetchSupportTicket,
    fetchSupportTickets,
    formatSupportTicketDate,
    formatSupportTicketSeverity,
    formatSupportTicketStatus,
    SupportTicketDetail,
    SupportTicketSeverity,
    SupportTicketStatus,
    SupportTicketSummary,
} from "../services/supportTicketsService";
import "../styles/feature-requests.css";
import "../styles/support-tickets.css";

const DEFAULT_PAGE_SIZE = 10;
const PAGE_SIZE_OPTIONS = [10, 20, 30, 40, 50];
const FILTER_DEBOUNCE_MS = 300;

const STATUS_FILTER_OPTIONS = [
    { value: "", label: "All" },
    { value: "RAISED", label: "Raised" },
    { value: "IN_PROGRESS", label: "In Progress" },
    { value: "CLOSED", label: "Closed" },
];

const SEVERITY_FILTER_OPTIONS = [
    { value: "", label: "All" },
    { value: "HIGH", label: "High" },
    { value: "MEDIUM", label: "Medium" },
    { value: "LOW", label: "Low" },
];

type Filters = {
    ticketNumber: string;
    title: string;
    raisedByFirstName: string;
    raisedByLastName: string;
    severity: SupportTicketSeverity | "";
    status: SupportTicketStatus | "";
};

const emptyFilters: Filters = {
    ticketNumber: "",
    title: "",
    raisedByFirstName: "",
    raisedByLastName: "",
    severity: "",
    status: "",
};

function ContactSupportIcon() {
    return (
        <svg
            className="support-tickets-contact-icon"
            viewBox="0 0 20 20"
            width="18"
            height="18"
            aria-hidden="true"
        >
            <circle cx="6" cy="6" r="3" fill="#E01E5A" />
            <circle cx="14" cy="6" r="3" fill="#36C5F0" />
            <circle cx="6" cy="14" r="3" fill="#2EB67D" />
            <circle cx="14" cy="14" r="3" fill="#ECB22E" />
        </svg>
    );
}

function findExactTicket(
    tickets: SupportTicketSummary[],
    ticketNumber: string,
): SupportTicketSummary | undefined {
    return tickets.find(
        (ticket) => ticket.ticketNumber.localeCompare(ticketNumber, undefined, { sensitivity: "accent" }) === 0,
    );
}

export default function CompanySupportPage() {
    const { isAuthenticated } = useAuth();
    const [searchParams, setSearchParams] = useSearchParams();
    const [tickets, setTickets] = useState<SupportTicketSummary[]>([]);
    const [page, setPage] = useState(0);
    const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
    const [totalPages, setTotalPages] = useState(0);
    const [totalElements, setTotalElements] = useState(0);
    const [initialLoading, setInitialLoading] = useState(true);
    const [refreshing, setRefreshing] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [filters, setFilters] = useState<Filters>(emptyFilters);
    const [debouncedFilters, setDebouncedFilters] = useState<Filters>(emptyFilters);
    const [submitOpen, setSubmitOpen] = useState(false);
    const [detail, setDetail] = useState<SupportTicketDetail | null>(null);
    const [detailOpen, setDetailOpen] = useState(false);
    const skipNextLoadRef = useRef(false);
    const hasLoadedOnceRef = useRef(false);
    const openedDeepLinkRef = useRef<string | null>(null);

    useEffect(() => {
        const handle = window.setTimeout(() => {
            setDebouncedFilters(filters);
            setPage(0);
        }, FILTER_DEBOUNCE_MS);
        return () => window.clearTimeout(handle);
    }, [filters]);

    const load = useCallback(async (pageNum: number, activeFilters: Filters, size: number) => {
        if (hasLoadedOnceRef.current) {
            setRefreshing(true);
        } else {
            setInitialLoading(true);
        }
        setError(null);
        try {
            const data = await fetchSupportTickets({
                ...activeFilters,
                status: activeFilters.status || undefined,
                severity: activeFilters.severity || undefined,
                page: pageNum,
                size,
            });
            setTickets(data.content);
            setTotalPages(data.totalPages);
            setTotalElements(data.totalElements);
            setPage(data.number);
            hasLoadedOnceRef.current = true;
        } catch (err: unknown) {
            setError(err instanceof Error ? err.message : "Failed to load support tickets.");
        } finally {
            setInitialLoading(false);
            setRefreshing(false);
        }
    }, []);

    useEffect(() => {
        if (skipNextLoadRef.current) {
            skipNextLoadRef.current = false;
            return;
        }
        void load(page, debouncedFilters, pageSize);
    }, [load, page, debouncedFilters, pageSize]);

    const handleSubmitted = useCallback(
        (created: SupportTicketSummary) => {
            if (page === 0) {
                setTickets((prev) =>
                    [created, ...prev.filter((t) => t.id !== created.id)].slice(0, pageSize),
                );
                setTotalElements((prev) => {
                    const next = prev + 1;
                    setTotalPages(Math.max(1, Math.ceil(next / pageSize)));
                    return next;
                });
                return;
            }
            skipNextLoadRef.current = true;
            setPage(0);
            setTickets([created]);
            setTotalElements(1);
            setTotalPages(1);
        },
        [page, pageSize],
    );

    const openDetail = useCallback(async (ticket: SupportTicketSummary) => {
        try {
            const full = await fetchSupportTicket(ticket.id);
            setDetail(full);
            setDetailOpen(true);
        } catch (err: unknown) {
            setError(err instanceof Error ? err.message : "Failed to load ticket details.");
        }
    }, []);

    const deepLinkTicket = searchParams.get("ticket")?.trim() ?? "";

    useEffect(() => {
        if (!deepLinkTicket || openedDeepLinkRef.current === deepLinkTicket) {
            return;
        }
        openedDeepLinkRef.current = deepLinkTicket;

        void (async () => {
            try {
                const data = await fetchSupportTickets({
                    ticketNumber: deepLinkTicket,
                    page: 0,
                    size: 20,
                });
                const match = findExactTicket(data.content, deepLinkTicket);
                if (!match) {
                    setError(`Ticket ${deepLinkTicket} was not found.`);
                    return;
                }
                await openDetail(match);
            } catch (err: unknown) {
                setError(err instanceof Error ? err.message : "Failed to open support ticket.");
            }
        })();
    }, [deepLinkTicket, openDetail]);

    const handleDetailClose = () => {
        setDetailOpen(false);
        if (searchParams.has("ticket")) {
            const next = new URLSearchParams(searchParams);
            next.delete("ticket");
            setSearchParams(next, { replace: true });
        }
    };

    const handleUpdated = (updated: SupportTicketDetail) => {
        setTickets((prev) =>
            prev.map((t) =>
                t.id === updated.id
                    ? {
                          ...t,
                          title: updated.title,
                          status: updated.status,
                          severity: updated.severity,
                          canEdit: updated.canEdit,
                          canClose: updated.canClose,
                      }
                    : t,
            ),
        );
        setDetail(updated);
        // Keep open tickets ahead of closed after status changes
        void load(page, debouncedFilters, pageSize);
    };

    const handlePageSizeChange = (size: number) => {
        setPageSize(size);
        setPage(0);
    };

    const handleContactSupport = () => {
        void openSupportEmail(isAuthenticated);
    };

    return (
        <main className="feature-requests-page support-tickets-page" data-testid="support-tickets-page">
            <div className="feature-requests-header page-bar">
                <div>
                    <h1 className="feature-requests-title">Support</h1>
                    <p className="feature-requests-subtitle">
                        Raise a ticket when you need help from Polaris support. Tickets are visible to
                        users in your school or company according to their role.
                    </p>
                </div>
                <div className="support-tickets-header-actions">
                    <button
                        type="button"
                        className="support-tickets-contact-btn"
                        data-testid="support-tickets-contact-support"
                        onClick={handleContactSupport}
                    >
                        <ContactSupportIcon />
                        Contact support
                    </button>
                    <button
                        type="button"
                        className="feature-requests-submit-btn"
                        data-testid="support-tickets-open-submit"
                        onClick={() => setSubmitOpen(true)}
                    >
                        Raise a ticket
                    </button>
                </div>
            </div>

            {error && (
                <div className="feature-requests-error" role="alert">
                    {error}
                </div>
            )}
            {initialLoading && <div className="feature-requests-loading">Loading tickets…</div>}

            {!initialLoading && (
                <div
                    className="support-tickets-table-wrap"
                    style={{ opacity: refreshing ? 0.55 : 1, transition: "opacity 0.15s ease" }}
                    aria-busy={refreshing}
                >
                    <table className="support-tickets-table" data-testid="support-tickets-table">
                        <thead>
                            <tr>
                                <th>Ticket number</th>
                                <th>Date</th>
                                <th>Title</th>
                                <th>Raised by</th>
                                <th>Severity</th>
                                <th>Status</th>
                            </tr>
                            <tr className="support-tickets-filter-row">
                                <th>
                                    <input
                                        className="support-tickets-filter-input"
                                        placeholder="Filter…"
                                        value={filters.ticketNumber}
                                        data-testid="support-tickets-filter-number"
                                        onChange={(e) =>
                                            setFilters((f) => ({ ...f, ticketNumber: e.target.value }))
                                        }
                                    />
                                </th>
                                <th />
                                <th>
                                    <input
                                        className="support-tickets-filter-input"
                                        placeholder="Filter…"
                                        value={filters.title}
                                        data-testid="support-tickets-filter-title"
                                        onChange={(e) =>
                                            setFilters((f) => ({ ...f, title: e.target.value }))
                                        }
                                    />
                                </th>
                                <th className="support-tickets-name-filters">
                                    <input
                                        className="support-tickets-filter-input"
                                        placeholder="First name"
                                        value={filters.raisedByFirstName}
                                        data-testid="support-tickets-filter-first-name"
                                        onChange={(e) =>
                                            setFilters((f) => ({
                                                ...f,
                                                raisedByFirstName: e.target.value,
                                            }))
                                        }
                                    />
                                    <input
                                        className="support-tickets-filter-input"
                                        placeholder="Last name"
                                        value={filters.raisedByLastName}
                                        data-testid="support-tickets-filter-last-name"
                                        onChange={(e) =>
                                            setFilters((f) => ({
                                                ...f,
                                                raisedByLastName: e.target.value,
                                            }))
                                        }
                                    />
                                </th>
                                <th>
                                    <SupportTicketSelect
                                        value={filters.severity}
                                        options={SEVERITY_FILTER_OPTIONS}
                                        size="compact"
                                        data-testid="support-tickets-filter-severity"
                                        onChange={(next) =>
                                            setFilters((f) => ({
                                                ...f,
                                                severity: next as SupportTicketSeverity | "",
                                            }))
                                        }
                                    />
                                </th>
                                <th>
                                    <SupportTicketSelect
                                        value={filters.status}
                                        options={STATUS_FILTER_OPTIONS}
                                        size="compact"
                                        data-testid="support-tickets-filter-status"
                                        onChange={(next) =>
                                            setFilters((f) => ({
                                                ...f,
                                                status: next as SupportTicketStatus | "",
                                            }))
                                        }
                                    />
                                </th>
                            </tr>
                        </thead>
                        <tbody>
                            {tickets.length === 0 ? (
                                <tr>
                                    <td colSpan={6} className="support-tickets-empty">
                                        No support tickets yet. Raise a ticket to get help from Polaris
                                        support.
                                    </td>
                                </tr>
                            ) : (
                                tickets.map((ticket) => (
                                    <tr
                                        key={ticket.id}
                                        className="support-tickets-row"
                                        data-testid={`support-ticket-row-${ticket.id}`}
                                        onClick={() => void openDetail(ticket)}
                                    >
                                        <td>{ticket.ticketNumber}</td>
                                        <td>{formatSupportTicketDate(ticket.createdAt)}</td>
                                        <td>{ticket.title}</td>
                                        <td>
                                            {[ticket.raisedByFirstName, ticket.raisedByLastName]
                                                .filter(Boolean)
                                                .join(" ") || "—"}
                                        </td>
                                        <td>
                                            <span
                                                className={`support-ticket-severity support-ticket-severity--${ticket.severity.toLowerCase()}`}
                                            >
                                                {formatSupportTicketSeverity(ticket.severity)}
                                            </span>
                                        </td>
                                        <td>
                                            <span
                                                className={`support-ticket-status support-ticket-status--${ticket.status.toLowerCase()}`}
                                            >
                                                {formatSupportTicketStatus(ticket.status)}
                                            </span>
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>
            )}

            {!initialLoading && (
                <Pagination
                    currentPage={page}
                    totalPages={Math.max(totalPages, 1)}
                    totalElements={totalElements}
                    pageSize={pageSize}
                    pageSizeOptions={PAGE_SIZE_OPTIONS}
                    onPageChange={setPage}
                    onPageSizeChange={handlePageSizeChange}
                />
            )}

            <SubmitSupportTicketModal
                open={submitOpen}
                onClose={() => setSubmitOpen(false)}
                onSubmitted={handleSubmitted}
            />
            <SupportTicketDetailModal
                open={detailOpen}
                ticket={detail}
                onClose={handleDetailClose}
                onUpdated={handleUpdated}
            />
        </main>
    );
}
